/**
 * DrawingTool.tsx
 * 
 * 职责：
 * 1. 提供绘图工具的用户界面组件
 * 2. 管理画笔颜色和粗细选择功能
 * 3. 与DrawingService服务交互，更新绘图属性
 * 4. 提供用户友好的绘图工具面板
 * 5. 根据当前活动状态条件渲染组件
 * 6. 维护本地状态与DrawingService状态的同步
 */

import React, { useState, useEffect } from 'react';
import { DrawingService } from '../services/DrawingService';

interface DrawingToolProps {
  drawingService: DrawingService;
  isActive: boolean;
  position?: { top: number, left: number };
}

const DrawingTool: React.FC<DrawingToolProps> = ({
  drawingService,
  isActive,
  position
}) => {
  // 使用React状态来跟踪当前选择的颜色和粗细
  const [currentColor, setCurrentColor] = useState<string>(drawingService.getSelectedPenColor());
  const [currentSize, setCurrentSize] = useState<number>(drawingService.getSelectedPenSize());
  // 添加动画状态
  const [isVisible, setIsVisible] = useState(false);
  
  // 当组件挂载时同步初始状态
  useEffect(() => {
    setCurrentColor(drawingService.getSelectedPenColor());
    setCurrentSize(drawingService.getSelectedPenSize());
  }, [drawingService]);
  
  // 监听isActive变化，添加延迟显示效果
  useEffect(() => {
    if (isActive) {
      // 延迟一帧显示，确保CSS过渡生效
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isActive]);
  
  // 颜色选项
  const colorOptions = [
    '#000000', // 黑色
    '#FF0000', // 红色
    '#0000FF', // 蓝色
    '#00FF00', // 绿色
    '#FFFF00', // 黄色
    '#FF00FF', // 品红
    '#00FFFF', // 青色
  ];
  
  // 线条粗细选项
  const sizeOptions = [1, 2, 3, 5, 8];
  
  // 防止事件冒泡，阻止点击工具面板选项时自动切换回选择工具
  const handlePanelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // 触发自定义事件
    document.dispatchEvent(new CustomEvent('tool-panel-interaction'));
  };
  
  // 处理颜色变更
  const handleColorChange = (color: string) => {
    drawingService.setSelectedPenColor(color);
    setCurrentColor(color); // 同时更新本地状态
    // 触发自定义事件
    document.dispatchEvent(new CustomEvent('tool-panel-interaction'));
  };
  
  // 处理粗细变更
  const handleSizeChange = (size: number) => {
    drawingService.setSelectedPenSize(size);
    setCurrentSize(size); // 同时更新本地状态
    // 触发自定义事件
    document.dispatchEvent(new CustomEvent('tool-panel-interaction'));
  };
  
  // 如果工具未激活，不显示面板
  if (!isActive) {
    return null;
  }
  
  // 计算面板位置
  const panelStyle = position ? {
    top: `${position.top}px`,
    left: `${position.left}px`,
  } : {
    top: '60px',
    left: '80px',
  };
  
  return (
    <div 
      className="drawing-tool-panel" 
      style={{
        position: 'absolute',
        ...panelStyle,
        backgroundColor: '#f5f5f5',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '10px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        zIndex: 9000,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(-10px)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none'
      }}
      onClick={handlePanelClick}
    >
      <div className="pen-color-selector" style={{ marginBottom: '10px' }}>
        <h4 style={{ marginBottom: '5px', fontSize: '14px' }}>画笔颜色</h4>
        <div className="color-options" style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          gap: '5px'
        }}>
          {colorOptions.map((color) => (
            <div
              key={color}
              onClick={(e) => { handlePanelClick(e); handleColorChange(color); }}
              style={{
                width: '20px',
                height: '20px',
                backgroundColor: color,
                border: `2px solid ${currentColor === color ? 'blue' : 'transparent'}`,
                borderRadius: '50%',
                cursor: 'pointer',
                transition: 'transform 0.2s ease',
                transform: `scale(${currentColor === color ? 1.1 : 1})`,
              }}
              title={color}
            />
          ))}
        </div>
      </div>
      
      <div className="pen-size-selector">
        <h4 style={{ marginBottom: '5px', fontSize: '14px' }}>画笔粗细</h4>
        <div className="size-options" style={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: '5px'
        }}>
          {sizeOptions.map((size) => (
            <div
              key={size}
              onClick={(e) => { handlePanelClick(e); handleSizeChange(size); }}
              style={{
                width: size * 3 + 10,
                height: size * 3 + 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${currentSize === size ? 'blue' : '#ccc'}`,
                borderRadius: '3px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                transform: `scale(${currentSize === size ? 1.05 : 1})`,
              }}
              title={`${size}px`}
            >
              <div style={{
                width: '100%',
                height: size,
                backgroundColor: 'black',
              }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DrawingTool; 